// REG-PPP — PPP Arrangement Registry (cross-cutting, V1).
// Replaces proposed DCIC-29. One thin, type-classified registry holds the
// whole FMLD PPP portfolio: infrastructure AND non-infrastructure
// arrangements. Registry-first: partner = DCIC-10 organisation; facility =
// DCIC-06, conditional on ppp_type = infrastructure. Progress/milestones are
// NOT captured here — they are reported via DCIC-12 Activity Report
// (activity_type = ppp_milestone, linked by ppp_id).
import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams, clampPayloadToCurrentScope, filterItemsByCurrentScope } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function toArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') {
    return value.split(',').map((part) => part.trim()).filter(Boolean);
  }
  return [value];
}

function humanize(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };
  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });
  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => String(item?.uuid ?? item?.id ?? '') === String(targetUuid))
        : candidate.items[0];
      if (match) return normalize(match);
    }

    if (candidate.uuid || candidate.id) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, collectionKey, defaultPerPage = 25 }) {
  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    const page = extractCollectionResponse(data, normalize);
    return { ...page, items: filterItemsByCurrentScope(page.items) };
  }

  return {
    collectionKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize);
      publishCollectionChange(collectionKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(collectionKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(collectionKey, { action: 'remove', uuid });
      return data;
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(collectionKey, { action: 'submit', uuid });
      return item;
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(collectionKey, { action: 'validate', uuid });
      return item;
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(collectionKey, { action: 'approve', uuid });
      return item;
    },

    async reject(uuid, notes = '') {
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes: trimOrNull(notes) });
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(collectionKey, { action: 'reject', uuid });
      return item;
    },
  };
}

export const PPP_TYPE_OPTIONS = [
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'service_delivery', label: 'Service Delivery' },
  { value: 'financing_co_investment', label: 'Financing / Co-Investment' },
  { value: 'input_supply', label: 'Input Supply' },
  { value: 'processing_value_addition', label: 'Processing / Value Addition' },
  { value: 'market_development', label: 'Market Development' },
  { value: 'research_innovation', label: 'Research / Innovation' },
  { value: 'capacity_building', label: 'Capacity Building' },
  { value: 'other', label: 'Other' },
];

export const PPP_ARRANGEMENT_MODEL_OPTIONS = [
  { value: 'bot', label: 'BOT' },
  { value: 'boo', label: 'BOO' },
  { value: 'concession', label: 'Concession' },
  { value: 'management_contract', label: 'Management Contract' },
  { value: 'joint_venture', label: 'Joint Venture' },
  { value: 'off_take', label: 'Off-Take' },
  { value: 'co_financing', label: 'Co-Financing' },
  { value: 'mou', label: 'MoU' },
  { value: 'other', label: 'Other' },
];

export const PPP_STATUS_OPTIONS = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'under_negotiation', label: 'Under Negotiation' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'completed', label: 'Completed' },
  { value: 'terminated', label: 'Terminated' },
];

function normalizePppArrangement(row = {}) {
  const dataStatus = row.data_status ?? row.dataStatus ?? 'draft';
  const partner = row.partner_org_id && typeof row.partner_org_id === 'object' ? row.partner_org_id : null;
  const facility = row.facility_id && typeof row.facility_id === 'object' ? row.facility_id : null;

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    ppp_arrangement_id: row.ppp_arrangement_id ?? row.pppArrangementId ?? '',
    pppArrangementId: row.ppp_arrangement_id ?? row.pppArrangementId ?? '',
    partner_org_id: partner?.uuid ?? row.partner_org_id ?? row.partnerOrgId ?? null,
    partnerOrgId: partner?.uuid ?? row.partner_org_id ?? row.partnerOrgId ?? null,
    partnerOrgName: partner?.organisation_name ?? partner?.name ?? row.partner_org_name ?? row.partnerOrgName ?? '',
    consortium_partner_org_ids: toArray(row.consortium_partner_org_ids ?? row.consortiumPartnerOrgIds),
    consortiumPartnerOrgIds: toArray(row.consortium_partner_org_ids ?? row.consortiumPartnerOrgIds),
    ppp_type: row.ppp_type ?? row.pppType ?? '',
    pppType: row.ppp_type ?? row.pppType ?? '',
    pppTypeLabel: humanize(row.ppp_type ?? row.pppType),
    arrangement_model: row.arrangement_model ?? row.arrangementModel ?? '',
    arrangementModel: row.arrangement_model ?? row.arrangementModel ?? '',
    arrangementModelLabel: humanize(row.arrangement_model ?? row.arrangementModel),
    facility_id: facility?.uuid ?? row.facility_id ?? row.facilityId ?? null,
    facilityId: facility?.uuid ?? row.facility_id ?? row.facilityId ?? null,
    facilityName: facility?.name ?? row.facility_name ?? row.facilityName ?? '',
    investment_value_ngn: toNumberOrNull(row.investment_value_ngn ?? row.investmentValueNgn),
    investmentValueNgn: toNumberOrNull(row.investment_value_ngn ?? row.investmentValueNgn),
    fmld_contribution_ngn: toNumberOrNull(row.fmld_contribution_ngn ?? row.fmldContributionNgn),
    fmldContributionNgn: toNumberOrNull(row.fmld_contribution_ngn ?? row.fmldContributionNgn),
    private_contribution_ngn: toNumberOrNull(row.private_contribution_ngn ?? row.privateContributionNgn),
    privateContributionNgn: toNumberOrNull(row.private_contribution_ngn ?? row.privateContributionNgn),
    commencement_date: row.commencement_date ?? row.commencementDate ?? '',
    commencementDate: row.commencement_date ?? row.commencementDate ?? '',
    duration_months: toNumberOrNull(row.duration_months ?? row.durationMonths),
    durationMonths: toNumberOrNull(row.duration_months ?? row.durationMonths),
    expiry_date: row.expiry_date ?? row.expiryDate ?? '',
    expiryDate: row.expiry_date ?? row.expiryDate ?? '',
    state: row.state ?? '',
    lga: row.lga ?? '',
    status: row.status ?? '',
    statusLabel: humanize(row.status),
    agreement_document: row.agreement_document ?? row.agreementDocument ?? '',
    agreementDocument: row.agreement_document ?? row.agreementDocument ?? '',
    agreement_document_file_name: row.agreement_document_file_name ?? row.agreementDocumentFileName ?? '',
    agreementDocumentFileName: row.agreement_document_file_name ?? row.agreementDocumentFileName ?? '',
    data_status: dataStatus,
    dataStatus,
    validationStatus: humanize(dataStatus),
    submitted_by: row.submitted_by ?? null,
    submitted_at: row.submitted_at ?? null,
    validated_by: row.validated_by ?? null,
    validated_at: row.validated_at ?? null,
    approved_by: row.approved_by ?? null,
    approved_at: row.approved_at ?? null,
    rejected_at: row.rejected_at ?? null,
    rejection_notes: row.rejection_notes ?? null,
    created_by: row.created_by ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    raw: row,
  };
}

function buildPppArrangementPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  const pppType = trimOrNull(scopedPayload.ppp_type ?? scopedPayload.pppType);
  return Object.fromEntries(
    Object.entries({
      state: trimOrNull(scopedPayload.state),
      lga: trimOrNull(scopedPayload.lga),
      partner_org_id: trimOrNull(scopedPayload.partner_org_id ?? scopedPayload.partnerOrgId),
      consortium_partner_org_ids: toArray(scopedPayload.consortium_partner_org_ids ?? scopedPayload.consortiumPartnerOrgIds),
      ppp_type: pppType,
      arrangement_model: trimOrNull(scopedPayload.arrangement_model ?? scopedPayload.arrangementModel),
      // Conditional per spec: shown and MANDATORY only when ppp_type = infrastructure.
      facility_id: pppType === 'infrastructure' ? trimOrNull(scopedPayload.facility_id ?? scopedPayload.facilityId) : null,
      investment_value_ngn: toNumberOrNull(scopedPayload.investment_value_ngn ?? scopedPayload.investmentValueNgn),
      fmld_contribution_ngn: toNumberOrNull(scopedPayload.fmld_contribution_ngn ?? scopedPayload.fmldContributionNgn),
      private_contribution_ngn: toNumberOrNull(scopedPayload.private_contribution_ngn ?? scopedPayload.privateContributionNgn),
      commencement_date: trimOrNull(scopedPayload.commencement_date ?? scopedPayload.commencementDate),
      duration_months: toNumberOrNull(scopedPayload.duration_months ?? scopedPayload.durationMonths),
      expiry_date: trimOrNull(scopedPayload.expiry_date ?? scopedPayload.expiryDate),
      status: trimOrNull(scopedPayload.status),
      agreement_document: trimOrNull(scopedPayload.agreement_document ?? scopedPayload.agreementDocument),
      agreement_document_file_name: trimOrNull(scopedPayload.agreement_document_file_name ?? scopedPayload.agreementDocumentFileName),
    }).filter(([, value]) => value !== null && value !== undefined)
  );
}

export const pppArrangementsApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/ppp-arrangements',
  normalize: normalizePppArrangement,
  buildPayload: buildPppArrangementPayload,
  collectionKey: 'ppp-arrangements',
  defaultPerPage: 25,
}), {
  basePath: '/v1/ppp-arrangements',
  collectionKey: 'ppp-arrangements',
  normalize: normalizePppArrangement,
  buildPayload: buildPppArrangementPayload,
});

export { buildPppArrangementPayload };
